import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const useEndCall = (
  pcRef,
  wsRef,
  localRef,
  setConnected,
  setHasJoined,
  setConnectionId,
  setIsVideoOn,
  setIsAudioOn,
  setIsRemoteVideoOn,
  setIsRemoteUserInRoom,
  setPermission,
  setRemoteVideoActive,
  handleLogs,
  isAdminOrCM,
  isRecording,
  captureFrame,
  stopRecordingAndUpload,
  stopRecordingAndUploadCm,
) => {
  const navigate = useNavigate();
  const isAdminOrCMRef = React.useRef(isAdminOrCM);
  const isRecordingRef = React.useRef(isRecording);

  React.useEffect(() => {
    isAdminOrCMRef.current = isAdminOrCM;
  }, [isAdminOrCM]);

  React.useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  /**
   * Closes all active connections (PeerConnection and WebSocket) and stops camera/mic tracks.
   * Cleans up the UI and resets local state to disconnected.
   */
  const handleEndCall = React.useCallback(
    async (data) => {
      if (isAdminOrCMRef.current) {
        // Upload applicant video with frame
        if (isRecordingRef.current) {
          const imageBlob = await captureFrame();
          await stopRecordingAndUpload(imageBlob);
        } else {
          const imageBlob = await captureFrame();
          await stopRecordingAndUpload(imageBlob);
        }
        // Upload CM video (only .mp4 file, no frames)
        if (stopRecordingAndUploadCm) {
          await stopRecordingAndUploadCm();
        }
      }

      // Close the peer connection to stop the P2P stream
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      // Close the WebSocket connection to the signaling server
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Stop and release camera/microphone tracks
      const m_track = localRef.current && localRef.current.srcObject;
      if (m_track) {
        m_track.getTracks().forEach((track) => track.stop());
        localRef.current.srcObject = null;
      }

      if (data && data.message) {
        handleLogs(`Meeting closed: ${data.message}`);
        toast.info(`${data.message}`, {
          autoClose: 5000,
          toastId: "meeting_closed",
        });
        // Navigate immediately for Admin/CM users - uploads happen in background
        if (
          isAdminOrCMRef.current &&
          data.type === "meeting_closed" &&
          data.message === "Meeting closed by HOST"
        ) {
          setTimeout(() => {
            navigate("/report/history");
            if (window.opener && !window.opener.closed) {
              window.opener.close();
            }
          }, 5000);
        }
      }

      // Reset state variables
      setConnected(false);
      setHasJoined(false);
      setConnectionId(null);
      setIsVideoOn(true);
      setIsAudioOn(true);
      setIsRemoteVideoOn(true);
      setIsRemoteUserInRoom(false);
      setPermission("unknown");
      setRemoteVideoActive(false);
      handleLogs("Stopped");
    },
    [
      pcRef,
      wsRef,
      localRef,
      setConnected,
      setHasJoined,
      setConnectionId,
      setIsVideoOn,
      setIsAudioOn,
      setIsRemoteVideoOn,
      setIsRemoteUserInRoom,
      setPermission,
      setRemoteVideoActive,
      handleLogs,
      captureFrame,
      stopRecordingAndUpload,
      stopRecordingAndUploadCm,
      navigate,
    ],
  );

  // useEffect(() => {
  //   // Try to request media on load so browser prompts for permission automatically
  //   (async () => {
  //     try {
  //       await handleRequestMedia();
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   })();
  //   // Clean up on unmount
  //   return () => handleRequestMedia();
  // }, [handleRequestMedia]);
  return { handleEndCall };
};
